import React from 'react';
import Webcam from 'react-webcam';
import { useDebouncedCallback } from 'use-debounce';

import { RATIO_16_9 } from 'constants/MediaConstants';

import type { MayBe } from 'modules/global/typing/common/common';

import Banner from './components/Banner';
import ConfirmRecord from './components/ConfirmRecord';
import ConfirmScreenshot from './components/ConfirmScreenshot';
import { CameraMode, type CameraModeEnum } from './components/types';
import { Wrapper, Close } from './components/UI';
import { b64toFile, canGenerateVideo } from './utils';

const BASE_CONSTRAINTS = {
  aspectRatio: {
    ideal: RATIO_16_9,
  },
};

type PhotoMakerProps = {
  disabled?: boolean;
  onClose: () => void;
  defaultMode?: CameraModeEnum;
  onError?: (error: unknown) => void;
  onGenerateMedia: (mode: CameraModeEnum, media: File | null) => void;
}

const PhotoMaker: React.FC<PhotoMakerProps> = ({ disabled = false, defaultMode = CameraMode.picture, onGenerateMedia, onClose, onError = () => { } }) => {
  const webcamRef = React.useRef<Webcam>(null);
  const recorderRef = React.useRef<MediaRecorder>(null);

  const [recChunks, setRecChunks] = React.useState<Blob[]>([]);
  const [picture, setPicture] = React.useState<File | null>(null);
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = React.useState<MayBe<string>>(undefined);
  const [recordingState, setRecordingState] = React.useState<RecordingState>('inactive');
  const [mode, setMode] = React.useState<CameraModeEnum>(canGenerateVideo() ? defaultMode : CameraMode.picture);

  const hasPicture = Boolean(picture);
  const hasDevice = devices.length > 0;
  const hasVideo = recordingState === 'inactive' && recChunks.length > 0;

  const constraints = React.useMemo(() => ({
    ...BASE_CONSTRAINTS,
    deviceId
  }), [deviceId]);

  const handleOnError = useDebouncedCallback(onError, 500);

  React.useEffect(() => {
    const getDevices = async () => {
      try {
        // This will ask user for permissions first, if not already granted
        const streams = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

        // Then we need to check if the user consented to *both* permissions
        const [cameraPermission, microphonePermission] = await Promise.all([
          navigator.permissions.query({ name: 'camera' }),
          navigator.permissions.query({ name: 'microphone' })
        ]);

        streams?.getTracks().forEach(track => track.stop());

        if (cameraPermission?.state === 'granted' && microphonePermission?.state === 'granted') {
          // Now get users devices
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          // Filter on video inputs
          const cameras = allDevices.filter(({ kind }) => kind === 'videoinput');

          const hasCameras = cameras.length > 0;
          if (hasCameras) {
            setDevices(cameras);
            setDeviceId(cameras[0].deviceId);
          } else {
            throw new Error('errors.noDevices');
          }
        } else {
          throw new Error('errors.noPermissions');
        }
      } catch (error) {
        handleOnError(error);
      }
    };
    getDevices();
  }, [handleOnError]);

  const handleOnCapture = () => {
    const screenshot = webcamRef.current?.getScreenshot();
    if (screenshot) {
      const [, base64] = screenshot.split(',');
      setPicture(b64toFile(base64, 'image/jpeg', `screenshot-${+new Date()}.jpeg`));
    }
  };

  const resetContent = () => {
    setPicture(null);
    setRecChunks([]);
  };

  const handleOnCancel = () => resetContent();

  const handleDataAvailable = React.useCallback(
    ({ data }: BlobEvent) => {
      if (data.size > 0) {
        setRecChunks(prev => prev.concat(data));
      }
    },
    [setRecChunks]
  );

  const handleOnPauseRecording = React.useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.pause();
      setRecordingState('paused');
    }
  }, [recorderRef]);

  const handleOnStopRecording = () => {
    setRecordingState('inactive');
    recorderRef.current?.stop();
  };

  const handleOnStartRecording = React.useCallback(() => {
    let mimeType = 'video/webm';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/mp4';
    }
    try {
      const stream = webcamRef.current?.stream;
      if (!stream) {
        return;
      }
      recorderRef.current = new MediaRecorder(stream, {
        mimeType,
      });
      recorderRef.current.addEventListener('dataavailable', handleDataAvailable);
      recorderRef.current.start();
      setRecordingState('recording');
      stream.getTracks().forEach(track => {
        track.addEventListener('ended', handleOnStopRecording);
      });
    } catch (error) {
      console.warn('Device camera error', error);
    }
  }, [handleDataAvailable, handleOnStopRecording, webcamRef, recorderRef]);

  const handleOnResumeRecording = React.useCallback(() => {
    if (recorderRef.current?.state === 'paused') {
      recorderRef.current.resume();
      setRecordingState('recording');
    }
  }, [recorderRef]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleOnPauseRecording();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleOnPauseRecording]);

  const handleOnCameraChange = () => {
    const index = devices.findIndex(({ deviceId: id }) => deviceId === id);
    const nextIndex = (index + 1) % devices.length;
    setDeviceId(devices[nextIndex].deviceId);
  };

  const getVideo = () => new File(recChunks, `video-${+new Date()}.mp4`, { type: 'video/mp4' });

  const handleOnOk = () => {
    const isPicture = mode === CameraMode.picture;
    const media = isPicture ? picture : getVideo();
    onGenerateMedia(mode, media);
    resetContent();
  };

  const handleOnModeChange = React.useCallback(
    (selectedMode: CameraModeEnum) => {
      if (!disabled) {
        setMode(selectedMode);
      }
    },
    [disabled]
  );

  return (
    hasDevice && (
      <Wrapper data-cy="use-camera-wrapper">
        {!hasPicture && !hasVideo && (
          <React.Fragment>
            {onClose && <Close onClick={onClose} data-cy="camera-close" />}
            <Webcam
              /* We record audio but we don't wan't to hear it */
              muted
              audio
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={constraints}
              onUserMediaError={handleOnError}
            />
            <Banner
              mode={mode}
              disabled={disabled}
              recordingState={recordingState}
              onCapture={handleOnCapture}
              onPause={handleOnPauseRecording}
              onModeChange={handleOnModeChange}
              onResume={handleOnResumeRecording}
              onCameraChange={handleOnCameraChange}
              onStopRecording={handleOnStopRecording}
              onStartRecording={handleOnStartRecording}
              showCamerasSwitcher={devices?.length > 1}
            />
          </React.Fragment>
        )}
        {hasPicture && (
          <ConfirmScreenshot picture={picture!} onCancel={handleOnCancel} onOk={handleOnOk} />
        )}

        {hasVideo && (
          <ConfirmRecord
            onOk={handleOnOk}
            onCancel={handleOnCancel}
            video={new Blob(recChunks, { type: 'video/mp4' })}
          />
        )}
      </Wrapper>
    )
  );
};

export default PhotoMaker;
